import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Modal } from 'antd';

import { Order, OrderReason } from '../../../../../../api-client';
import { RootState } from '../../../../../../application/store';
import { upsertOrder } from '../../../../../../slices/OrderSummary/orderSummarySlice';
import { toggleCheckoutDrawer } from '../../../../../../slices/System/systemSlice';
import { useCustomerOrders } from '../../../../../hooks/useCustomerOrders';
import { useOrder } from '../../../../../hooks/useOrder';
import { useCompleteDialog } from './hooks/useCompleteDialog';
import {
	ICompleteDialogForm,
	useCompleteDialogForm,
} from './hooks/useCompleteDialogForm';
import { CompleteDialogValidator } from './validators/CompleteDialogValidator';

import './shopping-complete-dialog.scss';

import BVComboBox from '../../../../BVControls/BVComboBox/bv-combo-box';
import BVInput from '../../../../BVControls/BVInput/bv-input';
import BVTextArea from '../../../../BVControls/BVTextArea/bv-text-area';

interface IShoppingCompleteDialog {
	visible: boolean;
	onClose: () => void;
}

const ShoppingCompleteDialog: React.FunctionComponent<
	IShoppingCompleteDialog
> = ({ visible, onClose }) => {
	const {
		handleUpsertOrder,
		upsertOrderLoading,
		handleFinalizeOrder,
		finalizeOrderLoading,
	} = useOrder();

	const { handleOrdersSummary } = useCustomerOrders();

	const { handleInitialize, handleChange, formValues } =
		useCompleteDialogForm();

	const { handleOrderReasons, orderReasons, orderReasonsLoading } =
		useCompleteDialog();

	const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);

	useEffect(() => {
		handleOrderReasons();
	}, []);

	useEffect(() => {
		if (visible) {
			let formInitializer: ICompleteDialogForm = {
				note: bvOrderSummary.note,
				poNumber: bvOrderSummary.poNumber,
				reason: bvOrderSummary.reason,
			};

			handleInitialize(formInitializer);
		}
	}, [visible]);

	const _handleResetForm = () => {
		handleInitialize({
			note: '',
			reason: undefined,
			poNumber: '',
		});

		setFormErrors({});

		onClose();
	};

	const dispatch = useDispatch();

	const [formErrors, setFormErrors] = useState<any>({});

	const history = useHistory();

	const _handleSubmitOrder = () => {
		const validationResult = CompleteDialogValidator.validateForm(formValues);

		setFormErrors(validationResult);

		if (Object.keys(validationResult).length === 0) {
			let completeOrder: Order = {
				...bvOrderSummary,
				poNumber: formValues.poNumber,
				reason: formValues.reason,
				note: formValues.note,
			};

			dispatch(upsertOrder(completeOrder));

			// UPDATE --> VALIDATE --> UPDATE STATUS
			//                     --> ALIGN --> UPDATE STATUS

			handleUpsertOrder(
				completeOrder,
				(orderId) => {
					let onFinalizeSuccess = () => {
						let onCompleteOrderSuccess = () => {
							_handleResetForm();

							dispatch(toggleCheckoutDrawer(false));

							history.push(`/?customer=${bvCustomer.sapCode}`);
						};

						handleOrdersSummary(
							{ customerId: bvCustomer.id },
							onCompleteOrderSuccess,
						);
					};

					let onFinalizeError = () => {
						_handleResetForm();
					};

					handleFinalizeOrder(orderId, onFinalizeSuccess, onFinalizeError);
				},
				false,
			);
		}
	};

	const bvCustomer = useSelector((state: RootState) => state.customer);

	return (
		<Modal
			title={`Complete order for ${bvCustomer.fullName}`}
			maskClosable={false}
			closable={false}
			confirmLoading={upsertOrderLoading || finalizeOrderLoading}
			cancelButtonProps={{
				disabled: upsertOrderLoading || finalizeOrderLoading,
			}}
			visible={visible}
			okText={'Complete Order'}
			cancelText={'Cancel'}
			onOk={_handleSubmitOrder}
			onCancel={_handleResetForm}
		>
			<div className="shopping-complete-dialog-container">
				<div className="form-group">
					<BVInput
						label={'PO Number:'}
						errorMessage={formErrors.poNumber}
						required
						properties={{
							placeholder: 'PO Number...',
							disabled: upsertOrderLoading || finalizeOrderLoading,
							value: formValues.poNumber,
							onChange: (e: any) => handleChange('poNumber', e.target.value),
							maxLength: 35,
						}}
					/>
				</div>
				<div className="form-group">
					<BVComboBox
						label={'Order Reason:'}
						errorMessage={formErrors.reason}
						required
						data={_.map(orderReasons, (dataItem: OrderReason) => ({
							label: dataItem.description,
							value: dataItem.code,
						}))}
						properties={{
							listHeight: 200,
							placeholder: 'Select an order reason...',
							disabled:
								orderReasonsLoading ||
								upsertOrderLoading ||
								finalizeOrderLoading,
							loading: orderReasonsLoading,
							value: formValues.reason?.code,
							onChange: (value: string) => {
								const reasonEntry =
									orderReasons &&
									orderReasons.find(
										(entry: OrderReason) => entry.code === value,
									);

								handleChange('reason', reasonEntry);
							},
						}}
					/>
				</div>
				<div className="form-group">
					<BVTextArea
						label={'Internal Note for CS:'}
						properties={{
							placeholder: 'Internal note...',
							disabled: upsertOrderLoading || finalizeOrderLoading,
							value: formValues.note,
							onChange: (e: any) => handleChange('note', e.target.value),
						}}
					/>
				</div>
			</div>
		</Modal>
	);
};

export default ShoppingCompleteDialog;
